/**
 * Identity-type detail, the per-type drilldown reached from a card on the "choose an identity type"
 * guide. One page component, three data-driven entries (workload_identity · agent · api_key), so every
 * type explains itself in the same plain-language shape: the one idea, the checks each request passes,
 * an optional flow, why it's safe, and what you bring vs. what we run.
 *
 * Content is executive-plain and describes the platform's bounded model confidently, and, by rule
 * (CLAUDE.md), it stays HONEST about where a type is weaker (an API key can't prove it wasn't copied, so
 * writes are a deny-by-default escape hatch and it's steered to a workload identity). The actionable Register
 * CTA only ever fires a flow that works. Sentence case throughout; AI/API/OIDC and the cloud proper nouns are
 * allowlisted by the UI-convention check. All type is at the 13px floor (`text-xs`) or larger.
 */

import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { getRouteApi, Link } from "@tanstack/react-router";
import { ArrowRight, Bot, Check, Cloud, Network, Server, ShieldCheck, User, Wrench } from "lucide-react";
import { Breadcrumbs, Button, InfoNote, PageHeader, SectionCard, cn } from "@pureid/ui";
import { useDocumentTitle } from "@/lib/use-document-title";
import { KIND_META } from "../agent-kind";
import type { AgentKind } from "../schemas";

const routeApi = getRouteApi("/_authed/tenants/$slug/agents/guide/$type");
const tenantRouteApi = getRouteApi("/_authed/tenants/$slug");

// ── Content model ──────────────────────────────────────────────────────────────
interface Check {
  title: string;
  body: string;
}
interface FlowNode {
  icon: LucideIcon;
  role: string;
  who: string;
  lim: string;
  accent?: boolean;
}
interface Principle {
  eyebrow: string;
  title: string;
  body: string;
}
interface TypeDetail {
  title: string;
  lede: string;
  idea: { chip: string; heading: string; body: ReactNode };
  atAGlance: string[];
  checksHeading: string;
  checks: Check[];
  checksNote?: ReactNode;
  /** An optional quiet note rendered below the why-safe tiles (e.g. a "not to be confused with" aside). */
  extraNote?: ReactNode;
  flow?: { heading: string; intro: ReactNode; nodes: FlowNode[]; caption: string; revoke?: ReactNode };
  principlesHeading: string;
  principles: Principle[];
  bring: string[];
  run: string[];
  kicker: ReactNode;
  registerLabel: string;
}

const emphasis = (text: string) => <span className="font-medium text-foreground">{text}</span>;

const TYPE_DETAIL: Record<AgentKind, TypeDetail> = {
  agent: {
    title: "AI agents",
    lede: "A person hands work to an AI assistant that acts in their name, and it calls other agents and tools to get it done. Here's how it proves who it is, and stays inside exactly what it's allowed to do.",
    idea: {
      chip: "Permission slip · expires in minutes",
      heading: "An agent carries a permission slip, not a password.",
      body: (
        <>
          Instead of a password or a long-lived key it could leak, each agent is given a short-lived,
          signed {emphasis("permission slip")} that says exactly what it may do and for how long. A
          checkpoint verifies that slip on {emphasis("every single call")}, so access is proven each time,
          never assumed.
        </>
      ),
    },
    atAGlance: [
      "Acts as a named person",
      "A short-lived, bounded permission slip",
      "Big or costly actions pause for approval",
      "Revocable instantly, mid-task",
    ],
    checksHeading: "Every action passes four checks",
    checks: [
      {
        title: "Who are you?",
        body: "The agent presents a verifiable identity it already holds from your cloud or identity provider. We check it against the issuers you trust.",
      },
      {
        title: "Are you allowed to do this?",
        body: "The permission slip names the exact tasks, resources, and hard spending and volume limits. Anything outside those lines is denied.",
      },
      {
        title: "Is it really you?",
        body: "The slip is cryptographically tied to a key only that agent holds, so a copied or stolen slip is useless to anyone else; it can't be replayed.",
      },
      {
        title: "Still allowed, right now?",
        body: "A checkpoint re-checks the slip on every call and enforces the limits live. One switch can revoke an agent instantly, mid-task.",
      },
    ],
    checksNote: (
      <>
        <span className="font-medium text-foreground">One piece we deliberately don't issue:</span> the
        identity in the first check. An agent brings the identity it already has from your environment; we
        verify it. Everything about permission, proof, and enforcement is ours.
      </>
    ),
    flow: {
      heading: "An agent can hire a helper, but never hand over more than it holds",
      intro: (
        <>
          Real work fans out: one agent breaks a job into pieces and spawns workers for them. That's exactly
          where access usually leaks. Here, a helper's authority can only ever be {emphasis("narrower")} than
          its parent's: same tasks or fewer, a smaller budget, a shorter clock.
        </>
      ),
      nodes: [
        { icon: User, role: "Person", who: "Approves once", lim: "Sets the outer limits" },
        { icon: Bot, role: "Agent", who: "The planner", lim: "Acts within those limits" },
        { icon: Network, role: "Worker", who: "The helper", lim: "A strict subset, never more", accent: true },
        { icon: Wrench, role: "Tool", who: "The resource", lim: "Checkpoint verifies every call" },
      ],
      caption: "A helper can never widen the job: not a new task, a bigger cap, or a longer deadline.",
      revoke: (
        <>
          <span className="font-semibold text-danger">Pull the plug on the agent, and its whole team of
          workers stops at once.</span>{" "}
          Revoke the parent and its entire worker subtree stops on the next call, mid-task, without rotating
          any secret.
        </>
      ),
    },
    principlesHeading: "Why this is safe by design",
    principles: [
      { eyebrow: "Deny by default", title: "Least privilege", body: "An agent gets the minimum it needs. Nothing is granted that wasn't asked for and approved." },
      { eyebrow: "One switch", title: "Instant kill switch", body: "Revoke a person, one agent, or the whole fleet in one move, and every helper underneath stops with it." },
      { eyebrow: "Tamper-evident", title: "Full audit trail", body: "Every decision, allowed or denied, and why, is recorded so it can't be quietly altered afterward." },
      { eyebrow: "Approval-gated", title: "Big actions pause", body: "Large or costly actions can be held for a person to approve, cryptographically tied to what they're approving." },
    ],
    bring: [
      "Your agents, running in your own environment",
      "The identities they already have from your cloud or identity provider",
      "The limits and approval rules your business wants enforced",
    ],
    run: [
      "The permission model: bounded, short-lived, least-privilege slips",
      "The proof that a slip belongs to the agent presenting it",
      "The checkpoint that enforces every call, plus the kill switch",
      "The tamper-evident record of every decision",
    ],
    kicker: (
      <>
        In one line:{" "}
        <span className="text-foreground">
          your agents, your identities, our permission slip, checkpoint, and kill switch.
        </span>
      </>
    ),
    registerLabel: "Register an AI agent",
  },

  workload_identity: {
    title: "Workload identity",
    lede: "A system that runs on its own (a CI pipeline, a cron job, a cloud service), authenticating with an identity it already has, governed under the same bounded rules as everything else.",
    idea: {
      chip: "Federate it, or we issue one",
      heading: "Bring the identity your cloud gives it, or have us issue one. The limits come from here.",
      body: (
        <>
          If a workload already proves who it is through your platform (
          {emphasis("AWS, GCP, Azure, Kubernetes, or GitHub OIDC")}), there's no new machine password to lose: we
          verify the identity you already trust and wrap it in a short-lived, owned, budget-capped grant. When a
          workload has no such identity to bring, PureID can {emphasis("issue it a short-lived one")}, held to the
          very same bounded rules. Federation is the stronger path, so we steer you there first.
        </>
      ),
    },
    atAGlance: [
      "Federate your cloud identity, or we issue one",
      "Owned, short-lived, budget-capped grant",
      "Verified on every call",
      "Revocable without rotating a secret",
    ],
    checksHeading: "Every request passes four checks",
    checks: [
      {
        title: "Who are you?",
        body: "The workload presents a token from your cloud or platform. We verify it against the exact issuers you've registered as trusted; nothing else is accepted.",
      },
      {
        title: "Are you allowed to do this?",
        body: "The grant names the resources and actions the workload may reach, with hard volume caps. It's the least the job needs, and nothing more.",
      },
      {
        title: "Is it really you?",
        body: "The grant is tied to a key the workload holds, so a stolen token alone can't be used; it has to be presented by the real workload.",
      },
      {
        title: "Still allowed, right now?",
        body: "Grants are short-lived and re-checked on every call. Revoke the workload and its access stops immediately, without rotating a shared secret.",
      },
    ],
    checksNote: (
      <>
        <span className="font-medium text-foreground">Bring the identity, or let us issue it.</span> Our
        recommended path is federation: bring the identity your cloud already issues, and we verify it and
        govern what it's allowed to do. When there's no cloud identity to bring, PureID issues a short-lived one
        instead. Either way, the same first check applies and the same bounded grant follows.
      </>
    ),
    flow: {
      heading: "Your platform vouches for the workload, we bound what it can do",
      intro: (
        <>
          The workload never holds a standing key. Its identity is minted by your own cloud at run time; we
          check it and exchange it for a {emphasis("narrow, short-lived grant")} that a checkpoint enforces on
          every call.
        </>
      ),
      nodes: [
        { icon: Cloud, role: "Your cloud", who: "Issues identity", lim: "AWS · GCP · Azure · Kubernetes · OIDC" },
        { icon: ShieldCheck, role: "PureID", who: "Verifies and bounds", lim: "Owned · short-lived · capped", accent: true },
        { icon: Server, role: "Resource", who: "The target system", lim: "Checkpoint verifies every call" },
      ],
      caption: "No shared secret sits on the workload; the identity is proven fresh each run.",
    },
    principlesHeading: "Why this is safe by design",
    principles: [
      { eyebrow: "No standing keys", title: "Nothing durable to steal", body: "No long-lived or shared secret sits on the workload; its identity is proven fresh each run, so there's nothing persistent to lift." },
      { eyebrow: "Named owner", title: "Always accountable", body: "Every workload identity has a human owner on record, so nothing runs orphaned with no one responsible for it." },
      { eyebrow: "Enforced every call", title: "Scope and budget caps", body: "The grant is capped to the resources, actions, and volume the job needs, checked live, not just at start-up." },
      { eyebrow: "One switch", title: "Instant revoke", body: "Cut a workload off in one move, no rotating a shared key across every service that used it." },
    ],
    bring: [
      "Your workloads, CI jobs, cron tasks, cloud services",
      "The identity your cloud or platform already issues them, or let PureID issue one",
      "The resources and limits each one should be held to",
    ],
    run: [
      "Verification of your trusted issuers, or issuance of a short-lived identity when there's none to bring",
      "A short-lived, owned, least-privilege grant per workload",
      "The checkpoint that enforces every call, plus instant revoke",
      "The tamper-evident record of every decision",
    ],
    kicker: (
      <>
        In one line:{" "}
        <span className="text-foreground">
          bring the identity or let us issue it, we say how much, for how long, and to what.
        </span>
      </>
    ),
    registerLabel: "Register a workload identity",
  },

  api_key: {
    title: "API-key integration",
    lede: "A partner or script that only needs to read data with a static, long-lived key. It's the most basic option, so we deliberately fence it in.",
    idea: {
      chip: "Read-only · owned · audited",
      heading: "A static key can be copied and can't prove who's using it, so we limit what it can do.",
      body: (
        <>
          An API key is a long-lived secret. Unlike an agent or a workload identity,{" "}
          {emphasis("a copy of it works just like the original")}, so it can't prove the caller is the real
          holder. That's why we keep it deliberately narrow: read-only, tied to a named owner, capped to no more
          than the person who issued it, expiring on a set clock, and revocable in one click.
        </>
      ),
    },
    atAGlance: [
      "Read-only by default, writes are gated",
      "Tied to a named, accountable owner",
      "Capped to no more than its issuer",
      "Expires on a clock · revocable in one click",
    ],
    checksHeading: "How we keep a basic key safe",
    checks: [
      {
        title: "Who owns it?",
        body: "Every key is tied to a named owner who is accountable for it; no anonymous, ownerless secrets floating around.",
      },
      {
        title: "What can it do?",
        body: "Read-only, scoped to the data it needs, and capped so it can never hold more than the person who issued it.",
      },
      {
        title: "Is it really the holder?",
        body: "A static key can't prove that on its own (a copy looks the same), which is exactly why it's read-only and held to the least it needs.",
      },
      {
        title: "Still valid, right now?",
        body: "Keys expire on a set clock and can be revoked in one click, so a leaked key has a limited life and a fast kill.",
      },
    ],
    checksNote: (
      <>
        <span className="font-medium text-foreground">Prefer a workload identity when you can.</span> If the
        system can present an identity from your cloud or platform, use a workload identity instead; it
        proves possession and expires in minutes, which a static key can't. Must a partner or script{" "}
        {emphasis("write")}, not just read? Because a copy of a static key works just like the original, write access is{" "}
        {emphasis("off by default")}: a security admin has to enable it for the whole tenant. Even then a
        write key is fenced hard (a tight scope, a short clock, spend and rate caps, instant revoke, and never
        for irreversible actions), and it's always a step down from a workload identity, which we steer you to
        first.
      </>
    ),
    extraNote: (
      <>
        This is a key for a partner or script to read your data, not an admin automation key for your own
        tooling to reach the management API (those live under settings).
      </>
    ),
    principlesHeading: "What holds a key in check",
    principles: [
      { eyebrow: "Read by default", title: "Writes are off by default", body: "A key reads the data it's scoped to and can't change anything, so a leak can't be turned into damage. Write access is a deny-by-default escape hatch a security admin must deliberately enable." },
      { eyebrow: "Rank-capped", title: "Never more than its issuer", body: "A key can only ever hold a subset of the permissions of the person who created it, no privilege escalation through a key." },
      { eyebrow: "On a clock", title: "Expires and revocable", body: "Every key has an expiry and can be revoked instantly, so it never becomes a forgotten forever-secret." },
      { eyebrow: "Tamper-evident", title: "Every use audited", body: "What a key read, and when, is recorded, so misuse is visible after the fact, not invisible." },
    ],
    bring: [
      "The partner or script that needs to read your data",
      "A vault to keep the secret in, so it never has to leave it",
      "The narrow scope of data it should be able to read",
    ],
    run: [
      "A read-only key, capped to no more than its issuer",
      "An expiry clock and one-click revoke",
      "The audit record of everything the key reads",
      "A clear nudge toward a workload identity when one fits better",
    ],
    kicker: (
      <>
        In one line:{" "}
        <span className="text-foreground">
          the basic option, fenced in, read-only, owned, expiring, and audited.
        </span>
      </>
    ),
    registerLabel: "Register an API-key integration",
  },
};

/** Type guard for the `$type` route param, the route loader uses it to 404 unknown slugs. */
export function isAgentTypeDetail(type: string): type is AgentKind {
  return Object.prototype.hasOwnProperty.call(TYPE_DETAIL, type);
}

export function AgentTypeDetailPage() {
  const { slug, type } = routeApi.useParams();
  const { tenant } = tenantRouteApi.useRouteContext();

  const kind = isAgentTypeDetail(type) ? type : undefined;
  const detail = kind ? TYPE_DETAIL[kind] : undefined;
  useDocumentTitle([detail?.title ?? "Identity type", tenant.name]);

  if (!kind || !detail) {
    return (
      <div className="space-y-6">
        <PageHeader title="Identity type not found" description="That identity type doesn't exist." />
        <Link to="/tenants/$slug/agents/guide" params={{ slug }}>
          <Button size="sm">Back to identity types</Button>
        </Link>
      </div>
    );
  }

  const meta = KIND_META[kind];
  const MetaIcon = meta.icon;

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: "Tenants", href: "/tenants" },
          { label: tenant.name, href: `/tenants/${slug}` },
          { label: "Non-human identities", href: `/tenants/${slug}/non-human-identities` },
          { label: "Agent overview", href: `/tenants/${slug}/agents/guide` },
          { label: detail.title },
        ]}
      />

      <PageHeader
        title={detail.title}
        description={detail.lede}
        back={
          <Link
            to="/tenants/$slug/agents/guide"
            params={{ slug }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            &larr; Overview
          </Link>
        }
      />

      {/* The one idea (left) + an at-a-glance summary (right) so the card uses its full width. */}
      <SectionCard tone="brand">
        <div className="grid gap-0 md:grid-cols-[1.5fr_1fr]">
          <div className="space-y-3 p-6">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <MetaIcon className="h-3.5 w-3.5" aria-hidden />
              {detail.idea.chip}
            </span>
            <h2 className="text-lg font-semibold leading-snug text-foreground">
              {detail.idea.heading}
            </h2>
            <p className="text-sm text-muted-foreground">{detail.idea.body}</p>
          </div>
          <div className="border-t border-border p-6 md:border-l md:border-t-0">
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">At a glance</span>
            <ul className="mt-3 flex flex-col gap-2.5">
              {detail.atAGlance.map((item) => (
                <li key={item} className="flex gap-2.5 text-sm text-foreground">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SectionCard>

      {/* The checks. */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-foreground">{detail.checksHeading}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {detail.checks.map((check, i) => (
            <SectionCard key={check.title} tone="neutral" className="h-full">
              <div className="flex h-full gap-3.5 p-5">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-border bg-primary/5 text-sm font-semibold text-primary font-mono tabular-nums">
                  {i + 1}
                </span>
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-foreground">{check.title}</h3>
                  <p className="text-sm text-muted-foreground">{check.body}</p>
                </div>
              </div>
            </SectionCard>
          ))}
        </div>
        {detail.checksNote ? <InfoNote className="mt-3">{detail.checksNote}</InfoNote> : null}
      </div>

      {/* Optional flow. */}
      {detail.flow ? (
        <SectionCard tone="brand">
          <div className="space-y-4 p-6">
            <h2 className="text-lg font-semibold leading-snug text-foreground">{detail.flow.heading}</h2>
            <p className="text-sm text-muted-foreground">{detail.flow.intro}</p>

            <div className="rounded-xl border border-border bg-muted/20 p-4">
              <div
                className="flex flex-wrap items-stretch justify-center gap-x-2 gap-y-4"
                role="img"
                aria-label={detail.flow.nodes.map((n) => `${n.role}: ${n.who}`).join(", then ")}
              >
                {detail.flow.nodes.map((node, i) => (
                  <FlowStep key={node.role} node={node} last={i === detail.flow!.nodes.length - 1} />
                ))}
              </div>
              <p className="mt-3 text-center text-xs text-muted-foreground">{detail.flow.caption}</p>
            </div>

            {detail.flow.revoke ? (
              <div className="flex items-start gap-3 rounded-xl border border-danger/25 bg-danger/5 p-4">
                <span className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-danger" aria-hidden />
                <p className="text-sm text-muted-foreground">{detail.flow.revoke}</p>
              </div>
            ) : null}
          </div>
        </SectionCard>
      ) : null}

      {/* Why safe. */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-foreground">{detail.principlesHeading}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {detail.principles.map((p) => (
            <SectionCard key={p.title} tone="neutral" className="h-full">
              <div className="flex h-full flex-col gap-2 p-5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {p.eyebrow}
                </span>
                <h3 className="text-sm font-semibold text-foreground">{p.title}</h3>
                <p className="text-sm text-muted-foreground">{p.body}</p>
              </div>
            </SectionCard>
          ))}
        </div>
        {detail.extraNote ? <InfoNote className="mt-3">{detail.extraNote}</InfoNote> : null}
      </div>

      {/* Bring vs run. */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-foreground">What you bring, what we run</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <SectionCard tone="neutral" className="h-full">
            <div className="flex h-full flex-col gap-3 p-5">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">You bring</span>
              <BulletList items={detail.bring} tone="muted" />
            </div>
          </SectionCard>
          <SectionCard tone="brand" className="h-full">
            <div className="flex h-full flex-col gap-3 p-5">
              <span className="text-xs font-semibold uppercase tracking-wide text-primary">We provide</span>
              <BulletList items={detail.run} tone="brand" />
            </div>
          </SectionCard>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">{detail.kicker}</p>

      {/* End on action. */}
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/tenants/$slug/agents" params={{ slug }} search={{ register: kind }}>
          <Button size="sm">
            {detail.registerLabel} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </Link>
        <Link to="/tenants/$slug/agents/guide" params={{ slug }}>
          <Button variant="outline" size="sm">
            Compare all types
          </Button>
        </Link>
      </div>
    </div>
  );
}

function FlowStep({ node, last }: { node: FlowNode; last: boolean }) {
  const { icon: Icon } = node;
  return (
    <>
      <div className="flex w-28 flex-col items-center gap-1.5 text-center">
        <div
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-xl border",
            node.accent ? "border-primary/30 bg-primary/5" : "border-border bg-muted",
          )}
        >
          <Icon className={cn("h-5 w-5", node.accent ? "text-primary" : "text-muted-foreground")} aria-hidden />
        </div>
        <span className="text-xs font-semibold text-foreground">{node.role}</span>
        <span className="text-xs text-foreground/80">{node.who}</span>
        <span className="text-xs text-muted-foreground">{node.lim}</span>
      </div>
      {!last ? <ArrowRight className="mt-4 h-5 w-5 flex-shrink-0 self-start text-primary/70" aria-hidden /> : null}
    </>
  );
}

function BulletList({ items, tone }: { items: string[]; tone: "muted" | "brand" }) {
  return (
    <ul className="flex flex-col gap-2.5">
      {items.map((item) => (
        <li key={item} className="flex gap-2.5 text-sm text-foreground">
          <span
            className={cn(
              "mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-sm",
              tone === "brand" ? "bg-primary" : "bg-muted-foreground/50",
            )}
            aria-hidden
          />
          {item}
        </li>
      ))}
    </ul>
  );
}
